import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { GlobalStateService } from '../global/global-state.service';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public user: BehaviorSubject<any> = new BehaviorSubject(undefined);
  public baseURL = '';

  constructor(private _globalStateService: GlobalStateService, private _http: HttpClient) {
    this._globalStateService.user.subscribe({ next: (user) => { this.user.next(user) } });
    this.baseURL = this._globalStateService.GetBaseEndpoint('base');
  }

  public Login(data: any) {
    return this._http.post(`${this.baseURL}/auth/portal-login`, data);
  }

  public GetProfile(){
    return this._http.get(`${this.baseURL}/auth/profile`);
  }

  public ForgotPassword(data: any) {
    return this._http.post(`${this.baseURL}/auth/forgot-password/admin`, data);
  }

  public UpdateProfile(data: any) {
    return this._http.post(`${this.baseURL}/auth/update-profile-admin`, data);
  }



  public ResendVerificationEmail(id:string){
    return this._http.post(`${this.baseURL}/auth/resend-verification-email/${id}`,{});
  }

  public IsVerified(id:string){
    return this._http.get(`${this.baseURL}/companies/${id}/is_verified`);
  }

  // Session storage methods for unverified users
  // NOTE: This is different from the resend email timer (2 minutes)
  private readonly UNVERIFIED_SESSION_KEY = 'unverified_user_session';
  private readonly SESSION_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds - for session persistence

  public setUnverifiedUserSession(userData: any): void {
    const sessionData = {
      userId: userData._id,
      userData: userData,
      timestamp: Date.now(),
      expiresAt: Date.now() + this.SESSION_DURATION // 5 minutes for session storage
    };
    // Stored in browser's sessionStorage (survives page refresh, cleared when tab closes)
    sessionStorage.setItem(this.UNVERIFIED_SESSION_KEY, JSON.stringify(sessionData));
  }

  public getUnverifiedUserSession(): any | null {
    const sessionData = sessionStorage.getItem(this.UNVERIFIED_SESSION_KEY);
    if (!sessionData) return null;

    try {
      const parsed = JSON.parse(sessionData);
      if (Date.now() > parsed.expiresAt) {
        this.clearUnverifiedUserSession();
        return null;
      }
      return parsed;
    } catch (error) {
      this.clearUnverifiedUserSession();
      return null;
    }
  }

  public clearUnverifiedUserSession(): void {
    sessionStorage.removeItem(this.UNVERIFIED_SESSION_KEY);
  }

  public isUnverifiedSessionValid(): boolean {
    const session = this.getUnverifiedUserSession();
    return session !== null;
  }

  public getRemainingSessionTime(): number {
    const session = this.getUnverifiedUserSession();
    if (!session) return 0;
    return Math.max(0, session.expiresAt - Date.now());
  }

  /**
   * Check if user has a valid authentication token
   */
  public hasAuthToken(): boolean {
    const token = localStorage.getItem('authToken');
    return !!token;
  }

  /**
   * Validate authentication by checking user state first, then API if needed
   * Returns Observable<boolean> indicating if user is authenticated
   */
  public validateAuthentication(): Observable<boolean> {
    if (!this.hasAuthToken()) {
      return of(false);
    }

    // Check if user is already loaded in global state
    const currentUser = this._globalStateService.user.value;
    if (currentUser && currentUser._id) {
      // User is already loaded, no need to call API
      return of(true);
    }

    // User not loaded, call GetProfile API
    return this.GetProfile().pipe(
      map((response: any) => {
        if (response && response.data) {
          // Update user state with profile data
          this._globalStateService.SetLoginState(response.data, localStorage.getItem('authToken') || '');
          return true;
        }
        return false;
      }),
      catchError((error) => {
        console.error('Authentication validation failed:', error);
        // Clear invalid token and user state
        this.clearAuthState();
        return of(false);
      })
    );
  }

  /**
   * Clear authentication state and localStorage
   */
  public clearAuthState(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user_id');
    this._globalStateService.ClearAuthState();
  }

  /**
   * Enhanced logout method
   */
  public Logout(): void {
    this.clearAuthState();
    localStorage.clear();
    window.location.reload();
  }

  public GetSSOCode(code: string): Observable<any> {
    return this._http.get(`${this.baseURL}/auth/login-as-client/${code}`);
  }

}
